import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import {
  Row,
  Col,
  Card,
  Button,
  Typography,
  Tag,
  message,
  Pagination,
  Spin,
  Empty,
  Select,
  Space,
  Badge,
} from "antd";
import {
  ShoppingCartOutlined,
  StarFilled,
} from "@ant-design/icons";
import ProductDetailsModal from "./ProductDetailsModal";
import ProductRating from "./components/ProductRating";
import ProductCardMedia from "./components/ProductCardMedia";
import API_CONFIG from "../config/api.js";
import "./PublicStore.css";

const { Title, Text } = Typography;
const { Meta } = Card;
const { Option } = Select;

interface Product {
  id: number;
  name: string;
  online_description: string;
  online_price: number;
  sales_price?: number;
  stock_quantity: number;
  image_url: string;
  gallery_images?: string[];
  online_images?: string[]; // 🖼️ Galerie multi-images
  category_name: string;
  is_featured?: boolean;
  // 🔄 ÉTAPE 3: Nouveaux champs API pour prix robuste
  final_price?: number;
  has_stock?: boolean;
  stock_status?: "en_stock" | "rupture" | "sur_commande" | "disponible";
  // 🆕 Nouveaux champs pour gestion stock conditionnelle
  stock_management_mode?: "standard" | "pre_order" | "unlimited";
  available_for_purchase?: boolean;
  // Nouveaux champs pour les avis
  reviews_count?: number;
  reviews_rating_avg?: number;
  // 🆕 Variantes (declinaisons Taille x Couleur)
  is_variant_parent?: boolean;
  variant_attributes?: { name: string; values: string[] }[] | null;
  variants?: any[];
}

interface ProductCatalogProps {
  storeSlug: string;
  searchQuery: string;
  selectedCategory: string;
  productsPerPage: number;
  onCartUpdate: (count: number) => void;
}

// Composant pour description expandable (pour les cards uniquement)
interface ExpandableDescriptionProps {
  description: string;
  maxLength?: number;
}

const ExpandableDescription: React.FC<ExpandableDescriptionProps> = ({
  description,
  maxLength = 150,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Si la description est courte, on l'affiche simplement
  if (description.length <= maxLength) {
    return <Text style={{ marginBottom: 8, display: "block", whiteSpace: "pre-wrap" }}>{description}</Text>;
  }

  // Version tronquée
  const truncatedText = description.substring(0, maxLength);

  return (
    <Text style={{ marginBottom: 8, display: "block", whiteSpace: "pre-wrap" }}>
      {isExpanded ? description : `${truncatedText}...`}
      {" "}
      <Text
        style={{
          color: "#1890ff",
          cursor: "pointer",
          fontWeight: 500,
        }}
        onClick={(e) => {
          e.stopPropagation(); // Empêcher l'ouverture du modal
          setIsExpanded(!isExpanded);
        }}
      >
        {isExpanded ? "Voir moins" : "Voir plus"}
      </Text>
    </Text>
  );
};

const ProductCatalog: React.FC<ProductCatalogProps> = ({
  storeSlug,
  searchQuery,
  selectedCategory,
  productsPerPage,
  onCartUpdate,
}) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const catalogRef = useRef<HTMLDivElement>(null);
  const pageSize = Math.min(100, Math.max(1, Math.trunc(Number(productsPerPage)) || 12));
  const [pagination, setPagination] = useState({
    current: 1,
    total: 0,
  });
  const [sortBy, setSortBy] = useState("availability");
  const [addingToCart, setAddingToCart] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  // Cache simple pour éviter les refetch inutiles
  const productsCache = useRef<
    Map<
      string,
      {
        products: Product[];
        pagination: { current: number; total: number };
        timestamp: number;
      }
    >
  >(new Map());

  // Hook pour détecter la taille d'écran
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768); // Mobile breakpoint
    };

    checkIsMobile();
    window.addEventListener("resize", checkIsMobile);
    return () => window.removeEventListener("resize", checkIsMobile);
  }, []);

  // Hook pour lazy loading d'images
  const [imageObserver, setImageObserver] =
    useState<IntersectionObserver | null>(null);

  useEffect(() => {
    // Créer l'observer pour le lazy loading
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const img = entry.target as HTMLImageElement;
            const dataSrc = img.getAttribute("data-src");
            if (dataSrc) {
              img.src = dataSrc;
              img.removeAttribute("data-src");
              img.classList.remove("lazy-loading");
              img.classList.add("lazy-loaded");
              observer.unobserve(img);
            }
          }
        });
      },
      {
        rootMargin: "50px 0px", // Charger 50px avant que l'image soit visible
        threshold: 0.1,
      }
    );

    setImageObserver(observer);

    return () => {
      observer.disconnect();
    };
  }, []);

  // État pour la modal de détails produit
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [modalVisible, setModalVisible] = useState(false);

  // URLs sécurisées pour APIs publiques uniquement
  const STORE_API = API_CONFIG.PUBLIC_STORE_API;

  // Toute modification du périmètre repart de la première page.
  useEffect(() => {
    setPagination((prev) => ({ ...prev, current: 1 }));
  }, [storeSlug, searchQuery, selectedCategory, sortBy, pageSize]);

  useEffect(() => {
    const controller = new AbortController();
    const cacheKey = JSON.stringify([storeSlug, sortBy, searchQuery, selectedCategory, pagination.current, pageSize]);
    setLoading(true);
    const loadProducts = async () => {
      const cached = productsCache.current.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < 5 * 60 * 1000) {
        setProducts(cached.products);
        setPagination(cached.pagination);
        setLoading(false);
        return;
      }
      try {
        const params = new URLSearchParams({ sort: sortBy, page: String(pagination.current), limit: String(pageSize) });
        // Les produits mis en valeur sont déjà affichés au-dessus du catalogue,
        // sauf lorsqu'une recherche ou une catégorie est sélectionnée.
        if (!searchQuery && !selectedCategory) params.set("exclude_featured", "true");
        if (searchQuery) params.set("search", searchQuery);
        if (selectedCategory) params.set("category", selectedCategory);
        const response = await fetch(`${STORE_API}/${storeSlug}/products?${params}`, { signal: controller.signal });
        if (!response.ok) throw new Error("Chargement impossible");
        const data = await response.json();
        if (controller.signal.aborted) return;
        const newProducts = data.products || [];
        const newPagination = { current: pagination.current, total: Number(data.total ?? newProducts.length) };
        setProducts(newProducts);
        setPagination(newPagination);
        productsCache.current.set(cacheKey, { products: newProducts, pagination: newPagination, timestamp: Date.now() });
        if (productsCache.current.size > 20) {
          const oldest = productsCache.current.keys().next().value;
          if (oldest !== undefined) productsCache.current.delete(oldest);
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          console.error("Error fetching products:", error);
          message.error("Erreur lors du chargement des produits");
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    const timer = setTimeout(loadProducts, searchQuery ? 300 : 0);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [STORE_API, storeSlug, searchQuery, selectedCategory, sortBy, pagination.current, pageSize]);

  const addToCart = useCallback(
    async (productId: number, quantity: number = 1) => {
      setAddingToCart(productId);

      // --- LOGGING START ---

      let sessionId = localStorage.getItem("ecommerce_session_id");
      if (!sessionId) {
        sessionId =
          "session_" +
          Date.now() +
          "_" +
          Math.random().toString(36).substr(2, 9);
        localStorage.setItem("ecommerce_session_id", sessionId);
      }

      const url = `${STORE_API}/${storeSlug}/cart/add`;
      const requestOptions = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Session-ID": sessionId,
        },
        body: JSON.stringify({
          product_id: productId,
          quantity,
        }),
      };

      // --- LOGGING END ---

      try {
        const response = await fetch(url, requestOptions);

        if (response.ok) {
          const result = await response.json();

          message.success("Produit ajouté au panier");
          fetchCartCount();
        } else {
          const error = await response.json();
          console.error("🛒 [addToCart] API Error:", error);
          message.error(error.error || "Erreur lors de l'ajout au panier");
        }
      } catch (error) {
        // Cette erreur est celle que vous voyez : "TypeError: Failed to fetch"
        console.error("🛒 [addToCart] Network or CORS Error:", error);
        console.error("🛒 [addToCart] Error details:", {
          name: (error as Error).name,
          message: (error as Error).message,
          stack: (error as Error).stack,
        });

        message.error(
          `Erreur de connexion. Vérifiez que le serveur backend fonctionne sur ${STORE_API}`
        );
      } finally {
        setAddingToCart(null);
      }
    },
    [STORE_API, storeSlug]
  );

  const fetchCartCount = async () => {
    try {
      const sessionId = localStorage.getItem("ecommerce_session_id");
      if (!sessionId) return;

      const response = await fetch(`${STORE_API}/${storeSlug}/cart`, {
        headers: {
          "X-Session-ID": sessionId,
        },
      });

      if (response.ok) {
        const data = await response.json();
        // ✅ FIX: Check if data and data.items exist before reduce()
        const count =
          data && data.items && Array.isArray(data.items)
            ? data.items.reduce(
                (sum: number, item: any) => sum + item.quantity,
                0
              )
            : 0;
        onCartUpdate(count);
      }
    } catch (error) {
      console.error("Error fetching cart count:", error);
    }
  };

  // ✅ PAGINATION: Handler pour changement de page
  const handlePageChange = useCallback((page: number) => {
    setPagination((prev) => ({
      ...prev,
      current: page,
    }));

    // Garder les premiers produits visibles sous l'en-tête fixe.
    const catalog = catalogRef.current;
    if (catalog) {
      const header = catalog.closest(".public-store")?.querySelector(".public-store__header");
      const headerHeight = header?.getBoundingClientRect().height || 0;
      window.scrollTo({
        top: Math.max(0, window.scrollY + catalog.getBoundingClientRect().top - headerHeight - 16),
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
      });
    }
  }, []);

  // Composant LazyImage optimisé
  const LazyImage: React.FC<{
    src: string;
    alt: string;
    style: React.CSSProperties;
    placeholder?: string;
  }> = ({ src, alt, style, placeholder = "#f0f0f0" }) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [isError, setIsError] = useState(false);
    const imgRef = useRef<HTMLImageElement>(null);

    useEffect(() => {
      const img = imgRef.current;
      if (img && imageObserver) {
        // Placeholder pendant le chargement
        img.style.backgroundColor = placeholder;
        img.style.transition = "opacity 0.3s ease";
        img.style.opacity = "0.7";

        // Observer l'image pour lazy loading
        imageObserver.observe(img);

        // Event listeners pour le chargement
        const handleLoad = () => {
          setIsLoaded(true);
          img.style.opacity = "1";
          img.style.backgroundColor = "transparent";
        };

        const handleError = () => {
          setIsError(true);
          img.style.opacity = "1";
        };

        img.addEventListener("load", handleLoad);
        img.addEventListener("error", handleError);

        return () => {
          img.removeEventListener("load", handleLoad);
          img.removeEventListener("error", handleError);
          if (imageObserver) {
            imageObserver.unobserve(img);
          }
        };
      }
    }, [imageObserver, placeholder]);

    if (isError) {
      return (
        <div
          style={{
            ...style,
            backgroundColor: "#f0f0f0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#ccc",
            fontSize: 12,
          }}
        >
          Image non disponible
        </div>
      );
    }

    return (
      <img
        ref={imgRef}
        data-src={src}
        alt={alt}
        style={style}
        className="lazy-loading"
      />
    );
  };

  // Fonctions pour gérer la modal de détails produit
  const openProductDetails = (product: Product) => {
    setSelectedProduct(product);
    setModalVisible(true);
  };

  const closeProductDetails = () => {
    setModalVisible(false);
    setSelectedProduct(null);
  };

  const formatProductPrice = (product: Product) => {
    const displayPrice =
      product.final_price || product.online_price || product.sales_price || 0;
    const priceValue = Number(displayPrice);

    if (priceValue === 0) {
      return "Gratuit";
    }

    const formatted = new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "XOF",
    }).format(priceValue);

    // Produit a variantes : le prix affiche est le minimum des declinaisons.
    return product.is_variant_parent ? `À partir de ${formatted}` : formatted;
  };

  const getPurchaseLabel = (product: Product) => {
    // Produit a variantes : on ouvre le detail pour choisir taille/couleur.
    if (product.is_variant_parent) {
      return isMobile ? "Options" : "Choisir les options";
    }

    if (product.available_for_purchase === false) {
      return "Rupture";
    }

    if (product.stock_management_mode === "pre_order") {
      return isMobile ? "Precommander" : "Precommander";
    }

    return isMobile ? "Ajouter" : "Ajouter au panier";
  };

  // Clic sur le bouton d'achat d'une carte : produit a variantes -> ouvrir le
  // detail (selection taille/couleur) ; sinon ajout direct au panier.
  const handlePurchaseClick = (product: Product) => {
    if (product.is_variant_parent) {
      openProductDetails(product);
    } else {
      addToCart(product.id);
    }
  };

  const renderAvailabilityTag = (product: Product) => {
    // Produit a variantes : la fiche parent n'a ni stock_quantity ni
    // stock_management_mode propres ; on s'appuie sur la dispo agregee des declinaisons.
    if (product.is_variant_parent) {
      return product.available_for_purchase === false ? (
        <Tag color="red">Rupture</Tag>
      ) : (
        <Tag color="green">Disponible</Tag>
      );
    }

    if (product.stock_management_mode === "unlimited") {
      return <Tag color="cyan">Toujours disponible</Tag>;
    }

    if (product.stock_management_mode === "pre_order") {
      return <Tag color="orange">Sur commande</Tag>;
    }

    if (product.stock_quantity > 0) {
      return (
        <Tag color="green">
          {isMobile ? "En stock" : `En stock (${product.stock_quantity})`}
        </Tag>
      );
    }

    return <Tag color="red">Rupture</Tag>;
  };

  const renderProductCard = (product: Product) => (
    <Card
      hoverable={!isMobile}
      className={`public-store__product-card ${
        product.is_featured ? "public-store__product-card--featured" : ""
      }`}
      onClick={() => openProductDetails(product)}
      bodyStyle={{ padding: isMobile ? 12 : 16 }}
      cover={
        <ProductCardMedia
          productName={product.name}
          primaryImage={product.image_url}
          galleryImages={product.gallery_images || product.online_images}
          className="public-store__product-media"
          placeholderClassName="public-store__product-placeholder"
          onOpen={() => openProductDetails(product)}
        >
          {product.is_featured && (
            <div className="public-store__product-badge">
              <StarFilled />
              Coup de coeur
            </div>
          )}
        </ProductCardMedia>
      }
    >
      <div className="public-store__product-content">
        <div className="public-store__product-header">
          <Text className="public-store__product-title">{product.name}</Text>
        </div>

        <div className="public-store__product-tags">
          {product.category_name ? <Tag color="blue">{product.category_name}</Tag> : null}
          {renderAvailabilityTag(product)}
        </div>

        {product.online_description && (
          <div className="public-store__product-description">
            <ExpandableDescription
              description={product.online_description}
              maxLength={isMobile ? 78 : 140}
            />
          </div>
        )}

        {product.reviews_count !== undefined && product.reviews_count > 0 && (
          <div className="public-store__product-rating">
            <ProductRating
              rating={product.reviews_rating_avg || 0}
              reviewsCount={product.reviews_count}
              compact
              size="small"
            />
          </div>
        )}

        <div className="public-store__product-price">
          {formatProductPrice(product)}
        </div>

        <Button
          type="primary"
          icon={<ShoppingCartOutlined />}
          loading={addingToCart === product.id}
          disabled={
            !product.is_variant_parent &&
            product.available_for_purchase === false
          }
          className={`public-store__product-action ${
            product.stock_management_mode === "pre_order"
              ? "public-store__product-action--preorder"
              : ""
          }`}
          onClick={(e) => {
            e.stopPropagation();
            handlePurchaseClick(product);
          }}
        >
          {getPurchaseLabel(product)}
        </Button>
      </div>
    </Card>
  );

  const handleModalAddToCart = async (productId: number, quantity: number) => {
    await addToCart(productId, quantity);
    // On peut garder la modal ouverte après ajout au panier
  };

  // 🚫 ÉTAPE 2: handlePageChange supprimé - plus de pagination
  // const handlePageChange = (page: number) => {
  //   setPagination((prev) => ({
  //     ...prev,
  //     current: page,
  //   }));
  // };

  if (loading && products.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "3rem" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div ref={catalogRef} aria-busy={loading}>
      {/* Filters and Sort - Responsive */}
      {!isMobile ? (
        // VERSION DESKTOP - IDENTIQUE À L'ORIGINAL
        <div className="public-store__catalog-toolbar">
          <Title level={3} className="public-store__catalog-title">
            Nos Produits
            {pagination.total > 0 && (
              <Text type="secondary" className="public-store__catalog-count">
                ({pagination.total} produits)
              </Text>
            )}
          </Title>

          <Space className="public-store__catalog-sort">
            <Text>Trier par :</Text>
            <Select
              value={sortBy}
              onChange={setSortBy}
              className="public-store__catalog-select"
            >
              <Option value="availability">Disponibilité</Option>
              <Option value="in_stock_first">Avec du stock d&apos;abord</Option>
              <Option value="preorder_or_unlimited_first">
                Précommande / illimité d&apos;abord
              </Option>
              <Option value="out_of_stock_first">Sans stock d&apos;abord</Option>
              <Option value="name">Nom</Option>
              <Option value="price_asc">Prix croissant</Option>
              <Option value="price_desc">Prix décroissant</Option>
              <Option value="featured">⭐ Mis en avant</Option>
            </Select>
          </Space>
        </div>
      ) : (
        // VERSION MOBILE - Layout vertical
        <div className="public-store__catalog-toolbar">
          <Title level={4} className="public-store__catalog-title">
            Nos Produits
            {pagination.total > 0 && (
              <Text type="secondary" className="public-store__catalog-count">
                ({pagination.total})
              </Text>
            )}
          </Title>

          <div className="public-store__catalog-sort">
            <Text style={{ fontSize: 14 }}>Trier :</Text>
            <Select
              value={sortBy}
              onChange={setSortBy}
              className="public-store__catalog-select"
              size="large"
            >
              <Option value="availability">Disponibilité</Option>
              <Option value="in_stock_first">Avec du stock d&apos;abord</Option>
              <Option value="preorder_or_unlimited_first">
                Précommande / illimité d&apos;abord
              </Option>
              <Option value="out_of_stock_first">Sans stock d&apos;abord</Option>
              <Option value="name">Nom</Option>
              <Option value="price_asc">Prix ↑</Option>
              <Option value="price_desc">Prix ↓</Option>
              <Option value="featured">⭐ Featured</Option>
            </Select>
          </div>
        </div>
      )}

      {/* Products Grid */}
      {products.length === 0 ? (
        <Empty description="Aucun produit trouvé" style={{ padding: "3rem" }} />
      ) : (
        <>
          <Spin spinning={loading}>
          <Row gutter={isMobile ? [12, 12] : [16, 16]}>
            {products.map((product) => (
              <Col key={product.id} xs={12} sm={12} md={8} lg={6}>
                {renderProductCard(product)}
              </Col>
            ))}
          </Row>
          </Spin>

          {false && <Row gutter={[16, 16]}>
            {products.map((product) => (
              <Col key={product.id} xs={24} sm={12} md={8} lg={6}>
                <Badge.Ribbon
                  text={
                    <span>
                      <StarFilled style={{ marginRight: 4 }} />
                      Coup de cœur
                    </span>
                  }
                  color="gold"
                  style={{
                    display: product.is_featured ? "block" : "none",
                  }}
                >
                  <Card
                    hoverable
                    onClick={() => openProductDetails(product)}
                    style={{
                      height: "100%",
                      border: product.is_featured
                        ? "2px solid #faad14"
                        : "1px solid #f0f0f0",
                      boxShadow: product.is_featured
                        ? "0 4px 12px rgba(250, 173, 20, 0.15)"
                        : undefined,
                      transition: "all 0.3s ease",
                      transform: "translateY(0)",
                      cursor: "pointer",
                    }}
                    onMouseEnter={(e) => {
                      if (!isMobile) {
                        (e.currentTarget as HTMLElement).style.transform =
                          "translateY(-4px)";
                        (e.currentTarget as HTMLElement).style.boxShadow =
                          product.is_featured
                            ? "0 8px 25px rgba(250, 173, 20, 0.25)"
                            : "0 4px 15px rgba(0,0,0,0.1)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isMobile) {
                        (e.currentTarget as HTMLElement).style.transform =
                          "translateY(0)";
                        (e.currentTarget as HTMLElement).style.boxShadow =
                          product.is_featured
                            ? "0 4px 12px rgba(250, 173, 20, 0.15)"
                            : "0 1px 3px rgba(0,0,0,0.1)";
                      }
                    }}
                    cover={
                      <div style={{ position: "relative", overflow: "hidden" }}>
                        {product.image_url ? (
                          <>
                            {/* Image principale */}
                            <div style={{ position: "relative" }}>
                              <LazyImage
                                src={product.image_url}
                                alt={product.name}
                                style={{
                                  height: isMobile ? 180 : 250,
                                  objectFit: "cover",
                                  width: "100%",
                                  transition: "opacity 0.3s ease",
                                }}
                                placeholder={
                                  product.is_featured ? "#fff7e6" : "#f0f0f0"
                                }
                              />
                            </div>
                            {/* 🖼️ Image galerie au hover (si disponible) */}
                            {product.online_images && product.online_images.length > 0 && (
                              <div
                                className="product-hover-image-wrapper"
                                style={{
                                  position: "absolute",
                                  top: 0,
                                  left: 0,
                                  width: "100%",
                                  height: isMobile ? 180 : 250,
                                  opacity: 0,
                                  transition: "opacity 0.3s ease",
                                }}
                              >
                                <LazyImage
                                  src={product.online_images[0]}
                                  alt={`${product.name} - Vue 2`}
                                  style={{
                                    height: isMobile ? 180 : 250,
                                    objectFit: "cover",
                                    width: "100%",
                                  }}
                                  placeholder="#f0f0f0"
                                />
                              </div>
                            )}
                          </>
                        ) : (
                          <div
                            style={{
                              height: isMobile ? 180 : 250,
                              backgroundColor: "#f0f0f0",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#ccc",
                            }}
                          >
                            Pas d'image
                          </div>
                        )}

                        {product.is_featured && (
                          <div
                            style={{
                              position: "absolute",
                              top: 8,
                              left: 8,
                              background:
                                "linear-gradient(45deg, #faad14, #ffd53e)",
                              color: "white",
                              padding: "4px 8px",
                              borderRadius: "12px",
                              fontSize: "12px",
                              fontWeight: "bold",
                              display: "flex",
                              alignItems: "center",
                              boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                            }}
                          >
                            <StarFilled
                              style={{ marginRight: 4, fontSize: "10px" }}
                            />
                            FEATURED
                          </div>
                        )}
                      </div>
                    }
                    actions={[
                      <Space
                        key="actions"
                        direction="vertical"
                        style={{ width: "100%" }}
                        size={isMobile ? "small" : "middle"}
                      >
                        <Button
                          type={product.is_featured ? "primary" : "default"}
                          icon={<ShoppingCartOutlined />}
                          loading={addingToCart === product.id}
                          disabled={
                            !product.is_variant_parent &&
                            product.available_for_purchase === false
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePurchaseClick(product);
                          }}
                          size={isMobile ? "large" : "middle"}
                          style={{
                            width: "100%",
                            background: product.is_featured
                              ? "linear-gradient(45deg, #1890ff, #40a9ff)"
                              : product.stock_management_mode === "pre_order"
                              ? "linear-gradient(45deg, #fa8c16, #ffa940)"
                              : undefined,
                          }}
                        >
                          {/* 🆕 TEXTE BOUTON SELON LE MODE */}
                          {product.is_variant_parent
                            ? isMobile
                              ? "Options"
                              : "Choisir les options"
                            : product.available_for_purchase === false
                            ? "Rupture"
                            : product.stock_management_mode === "pre_order"
                            ? isMobile
                              ? "Précommander"
                              : "📅 Précommander"
                            : product.stock_management_mode === "unlimited"
                            ? isMobile
                              ? "Ajouter"
                              : "Ajouter au panier"
                            : isMobile
                            ? "Ajouter"
                            : "Ajouter au panier"}
                        </Button>
                      </Space>,
                    ]}
                  >
                    <Meta
                      title={
                        <div>
                          <div
                            style={{
                              fontSize: 16,
                              fontWeight: "bold",
                              color: product.is_featured
                                ? "#faad14"
                                : "inherit",
                            }}
                          >
                            {product.is_featured && (
                              <StarFilled
                                style={{ marginRight: 6, color: "#faad14" }}
                              />
                            )}
                            {product.name}
                          </div>
                          <div style={{ marginTop: 4 }}>
                            <Tag color="blue">{product.category_name}</Tag>

                            {/* 🆕 BADGES DE STOCK CONDITIONNELS SELON LE MODE */}
                            {product.stock_management_mode === "unlimited" && (
                              <Tag color="cyan" icon={<span>∞</span>}>
                                Toujours disponible
                              </Tag>
                            )}

                            {product.stock_management_mode === "pre_order" && (
                              <Tag color="orange" icon={<span>🕐</span>}>
                                Sur commande
                              </Tag>
                            )}

                            {product.stock_management_mode === "standard" && (
                              <>
                                {product.stock_quantity > 0 && (
                                  <Tag color="green">
                                    ✓ En stock ({product.stock_quantity})
                                  </Tag>
                                )}
                                {product.stock_quantity === 0 && (
                                  <Tag color="red">✗ Rupture</Tag>
                                )}
                              </>
                            )}

                            {product.is_featured && (
                              <Tag
                                color="gold"
                                style={{
                                  fontWeight: "bold",
                                  background:
                                    "linear-gradient(45deg, #faad14, #ffd53e)",
                                  border: "none",
                                  color: "white",
                                }}
                              >
                                ⭐ Coup de cœur
                              </Tag>
                            )}
                          </div>
                        </div>
                      }
                      description={
                        <div>
                          {product.online_description && (
                            <ExpandableDescription
                              description={product.online_description}
                              maxLength={150}
                            />
                          )}

                          {/* Note produit (si avis disponibles) */}
                          {product.reviews_count !== undefined &&
                            product.reviews_count > 0 && (
                              <div style={{ marginBottom: 8 }}>
                                <ProductRating
                                  rating={product.reviews_rating_avg || 0}
                                  reviewsCount={product.reviews_count}
                                  compact
                                  size="small"
                                />
                              </div>
                            )}

                          <div
                            style={{
                              fontSize: 18,
                              fontWeight: "bold",
                              color: product.is_featured
                                ? "#faad14"
                                : "#52c41a",
                            }}
                          >
                            {(() => {
                              // 🔄 ÉTAPE 3: Utilisation final_price + fallbacks intelligents
                              const displayPrice =
                                product.final_price ||
                                product.online_price ||
                                product.sales_price ||
                                0;
                              const priceValue = Number(displayPrice);

                              if (priceValue === 0) {
                                return (
                                  <span
                                    style={{
                                      color: "#52c41a",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    Gratuit
                                  </span>
                                );
                              }

                              return new Intl.NumberFormat("fr-FR", {
                                style: "currency",
                                currency: "XOF",
                              }).format(priceValue);
                            })()}
                          </div>
                        </div>
                      }
                    />
                  </Card>
                </Badge.Ribbon>
              </Col>
            ))}
          </Row>}

          {/* ✅ PAGINATION: Composant de pagination */}
          {pagination.total > pageSize && (
            <div className="public-store__pagination">
              <Pagination
                current={pagination.current}
                total={pagination.total}
                pageSize={pageSize}
                onChange={handlePageChange}
                showSizeChanger={false}
                simple={isMobile}
                responsive
                showLessItems={isMobile}
                size={isMobile ? "small" : "default"}
                showTotal={
                  isMobile
                    ? undefined
                    : (total, range) => `${range[0]}-${range[1]} sur ${total} produits`
                }
              />
              {isMobile && (
                <Text className="public-store__pagination-summary">
                  {`${(pagination.current - 1) * pageSize + 1}-${Math.min(
                    pagination.current * pageSize,
                    pagination.total
                  )} sur ${pagination.total} produits`}
                </Text>
              )}
            </div>
          )}

          {/* Affichage du total si moins de produits que le seuil */}
          {pagination.total > 0 && pagination.total <= productsPerPage && (
            <div className="public-store__results-count">
              <Text>
                📦 {pagination.total} produit{pagination.total > 1 ? "s" : ""}{" "}
                affiché{pagination.total > 1 ? "s" : ""}
              </Text>
            </div>
          )}
        </>
      )}

      {/* Modal de détails produit */}
      <ProductDetailsModal
        visible={modalVisible}
        product={selectedProduct}
        onClose={closeProductDetails}
        onAddToCart={handleModalAddToCart}
        storeSlug={storeSlug}
      />

      {/* 🖼️ CSS Galerie Multi-Images Hover Effect */}
    </div>
  );
};

export default ProductCatalog;
